import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SelectField, ToggleField } from '../shared/FormField';
import { buildSpeciesOptions, parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

export default function EditProductionPurposeModal({ isOpen, onClose, onSave, row, species = [] }) {
  const [form, setForm] = useState({
    species_uuid: '',
    purpose_code: '',
    purpose_label: '',
    seasonal_flag: false,
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!row) return;

    setForm({
      species_uuid: row.species_uuid ?? '',
      purpose_code: row.purpose_code ?? '',
      purpose_label: row.purpose_label ?? '',
      seasonal_flag: row.seasonal_flag ?? false,
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const speciesOptions = buildSpeciesOptions(species);

  const submit = async () => {
    if (submitting || !row?.uuid) return;

    if (!form.species_uuid || !form.purpose_code.trim() || !form.purpose_label.trim()) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid: row.uuid,
        species_uuid: form.species_uuid,
        purpose_code: form.purpose_code.trim(),
        purpose_label: form.purpose_label.trim(),
        seasonal_flag: form.seasonal_flag,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to update production purpose.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title="Edit Production Purpose"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Production Purpose Updated"
      successMessage="The production purpose changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Purpose Code"
        required
        value={form.purpose_code}
        onChange={set('purpose_code')}
        readOnly
        className="cursor-not-allowed bg-gray-50 font-mono"
        note="System identifier - cannot be edited after creation."
      />
      <SelectField
        label="Species"
        required
        value={form.species_uuid}
        onChange={set('species_uuid')}
        options={speciesOptions}
        placeholder="Select Species"
      />
      <TextField
        label="Purpose Label"
        required
        value={form.purpose_label}
        onChange={set('purpose_label')}
        placeholder="e.g. Beef production"
      />
      <ToggleField
        label="Seasonal Flag"
        value={form.seasonal_flag}
        onChange={set('seasonal_flag')}
        note="Toggle on when the purpose is seasonal."
      />
    </Modal>
  );
}
